import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Message } from "primereact/message";

import StationSubscriptionsPanel from "../AppUsers/notifications/StationSubscriptionsPanel";
import StationSubscriberSearch from "./StationSubscriberSearch";
import UserLookup from "./UserLookup";
import UserHeading from "./UserHeading";
import { resolveToEmaid } from "./useResolvedUser";

const AdminSubscriptionsPage = () => {
  const { emaid } = useParams();
  const navigate = useNavigate();
  const [resolving, setResolving] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  /** Accepts either identifier: an address is resolved to the eMAID the APIs are keyed by. */
  const lookup = async (entered: string) => {
    setLookupError(null);
    setResolving(true);
    const { emaid: resolved, error } = await resolveToEmaid(entered);
    setResolving(false);
    if (resolved) navigate(`/notifications/subscriptions/${encodeURIComponent(resolved)}`);
    else setLookupError(error);
  };

  return (
    <div className="surface-section px-4 py-5 md:px-6 lg:px-8">
      <div className="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-3 mb-4">
        <div>
          <div className="text-2xl font-semibold">Station subscriptions</div>
          <p className="text-color-secondary mt-2 mb-0">
            What a person asked to be told about, and the ability to stop it on their behalf.
          </p>
        </div>
        {emaid && (
          <div className="flex gap-2">
            <Button
              label="Change history"
              icon="pi pi-history"
              outlined
              onClick={() => navigate(`/notifications/audit/${encodeURIComponent(emaid)}`)}
            />
            <Button
              label="Open user"
              icon="pi pi-user"
              outlined
              onClick={() => navigate(`/users/${encodeURIComponent(emaid)}`)}
            />
          </div>
        )}
      </div>

      <div className="mb-4">
        <UserLookup
          value={emaid ?? ""}
          onSubmit={lookup}
          resolving={resolving}
          error={lookupError}
          hint="An email is resolved to the eMAID the APIs are keyed by."
        />
      </div>

      {emaid ? (
        <Card className="shadow-1" title={<UserHeading emaid={emaid} />}>
          <StationSubscriptionsPanel userId={emaid} />
        </Card>
      ) : (
        <div data-testid="no-user-chosen">
          <Message severity="info" text="Enter an eMAID or email above to see what that person is subscribed to." />
        </div>
      )}

      <Card className="shadow-1 mt-4" title="Search by station" subTitle="Who is subscribed to a station, and which of them own it">
        <StationSubscriberSearch />
      </Card>
    </div>
  );
};

export default AdminSubscriptionsPage;
